import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Rocket } from 'lucide-react';
import { useProject } from '@/hooks/queries/useProject';
import { useTriggerBuild } from '@/hooks/mutations/useTriggerBuild';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LoadingState from '@/components/common/LoadingState';

interface DeploymentFormData {
  branch: string;
  commit_sha: string;
}

export default function NewDeployment() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(projectId);
  const triggerBuild = useTriggerBuild();

  const {
    register,
    handleSubmit,
  } = useForm<DeploymentFormData>({
    defaultValues: {
      branch: 'main',
      commit_sha: '',
    },
  });

  const onSubmit = async (data: DeploymentFormData) => {
    if (!projectId) return;

    try {
      const result = await triggerBuild.mutateAsync({
        projectId: projectId,
        data: {
          branch: data.branch || undefined,
          commit_sha: data.commit_sha || undefined,
        },
      });

      // Navigate to the deployment details page
      if (result.deployment?.id) {
        navigate(`/projects/${projectId}/deployments/${result.deployment.id}`);
      } else {
        navigate(`/projects/${projectId}`);
      }
    } catch (error) {
      console.error('Failed to trigger build:', error);
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Project not found</p>
        <Button onClick={() => navigate('/')} className="mt-4">
          Go to Home
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${projectId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Trigger New Deployment</h1>
          <p className="text-gray-600 mt-1">{project.name}</p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Deployment Configuration</CardTitle>
          <CardDescription>
            Trigger a new build and deployment for your project
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="branch" className="text-sm font-medium">
                Branch (optional)
              </label>
              <Input
                id="branch"
                placeholder="main"
                {...register('branch')}
              />
              <p className="text-xs text-gray-500">
                Leave empty to use the default branch
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="commit_sha" className="text-sm font-medium">
                Commit SHA (optional)
              </label>
              <Input
                id="commit_sha"
                placeholder="Leave empty for latest commit"
                {...register('commit_sha')}
              />
              <p className="text-xs text-gray-500">
                Leave empty to deploy the latest commit
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={triggerBuild.isPending}
                className="flex-1"
              >
                <Rocket className="h-4 w-4 mr-2" />
                {triggerBuild.isPending ? 'Triggering...' : 'Trigger Deployment'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/projects/${projectId}`)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Project Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Project Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Repository:</span>
            <a
              href={project.github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {project.github_url}
            </a>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Project ID:</span>
            <code className="text-xs bg-gray-100 px-2 py-1 rounded">{project.id}</code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
